import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Success } from '../components/FancyToast';
import { Error } from '../components/FancyToast';

import useUserIdentity from "../hooks/useUserIdentity";

import useDevice from "hooks/useDevice";
// import useRealtime from 'hooks/useRealtime';

// import { RealtimePacketType } from 'contexts/realtime_context';
// import { MessageProps } from 'stores/messages_store'

// urlB64ToUint8Array is a magic function that will encode the base64 public key
// to Array buffer which is needed by the subscription option
const urlB64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}

// i know, i know, but it's public anyway
const VAPID_PUBLIC_KEY = "BAN-bqjaqXXIgh-OWewQtYmQRoM54Yb3LonuIIILNtZqh8EmRDMI3nBcZtnlYNvScEYGrIBxL9In3yzco3EKHO4";


export type ServiceWorkerContextType = {
    isSubscribed: () => boolean;
    askForPermission: () => void;
    unsupported: boolean;
    // recents: RecentType[];
    // addRecent?: (recent: RecentType) => void;
    clearServiceWorker: () => Promise<number>;
}


export const ServiceWorkerContext = React.createContext<ServiceWorkerContextType>({
    isSubscribed: () => false,
    askForPermission: () => { throw "Not  implemented" },
    clearServiceWorker: () => { throw "Not  implemented" },
    unsupported: false, // hope for the best
});


export const ServiceWorkerProvider = observer(({ children }: { children: React.ReactNode }) => {
    const { currentIdentity } = useUserIdentity();
    const [subscription, setSubscriptionState] = useState(null as PushSubscription | null);
    const [registration, setRegistration] = useState(null as ServiceWorkerRegistration | null);
    const [unsupported, setUnsupported] = useState(false);
    const [listening, setListening] = useState(false);
    const { updateSub } = useDevice();
    //    const { forwardUpdate } = useRealtime();
    const navigate = useNavigate();

    const setSubscription = useCallback((sub: PushSubscription) => {
        setSubscriptionState(sub);
        updateSub(sub);
    }, [updateSub]);

    const subscribeToNotifications = useCallback(() => {
        if (!registration) {
            return
        }

        registration.pushManager.getSubscription().then((sub) => {
            if (!sub) {
                console.log("subscribe");
                console.trace("how'd you get here:!");
                const publicKey = urlB64ToUint8Array(VAPID_PUBLIC_KEY)
                // console.log("publicKey: %o", publicKey);
                // Subscribe for push notifications.
                registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: publicKey,
                }).then((created) => {
                    console.log("subsc: %o", created);
                    setSubscription(created);
                }).catch((err) => {
                    console.log("err: %o", err);
                }).finally(() => {
                    console.log("finally!!");
                });
            }
            else {
                setSubscription(sub);
            }
        });


    }, [registration]);


    const updateRegistration = useCallback((reg: ServiceWorkerRegistration) => {
        setRegistration(reg);
        reg.pushManager.getSubscription().then((sub) => {
            if(sub) {
                setSubscription(sub);
            }
        });
    }, [setRegistration, subscribeToNotifications]);


    function startListening() {
        // console.log("adding event listener");
        if (!listening) {
            setListening(true);
            console.log("service worker startListening() init");

            if(!navigator.serviceWorker) {
                console.error("No service worker");
                return;
            }
            console.log("service worker startListening() going to start listening");
            navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
                if (event.data.type == 'redirect') {
                    Success("Redirecting");
                    navigate(event.data.url);
                }
            });
        }
    }

    useEffect(() => {
        if (currentIdentity?.key) {
            const url = import.meta.env.MODE === 'production' ? '/service.js' : '/dev-sw.js?dev-sw';
            const _type = import.meta.env.MODE === 'production' ? 'classic' : 'module';
            console.log("registering service worker: %o, type: %s", url, _type);
            navigator.serviceWorker.register(url, { scope: '/', type: _type })
                .then((reg) => {
                    // console.log('sub setting registration state: %o', reg);
                    updateRegistration(reg);
                    startListening();
                })
                .catch((err) => {
                    console.error("sub Service worker registration failed: %o", err);
                    setUnsupported(true);
                });
        }

    }, [updateRegistration, currentIdentity?.key]);


    async function registerServiceWorker() {

        if (!registration) {
            console.error("Want me to register a null registerion");
            return;
        }

        if (unsupported) {
            console.error("We have a registration, but we're unsupported? registration:",
                registration);
            return;
        }

        subscribeToNotifications();
    }

    function isSubscribed() {
        // console.log("isSubscribed: %o", subscription);
        return subscription != null;
    }

    async function clearServiceWorker() {

        return navigator.serviceWorker.getRegistrations().then(registrations => {
            const count = registrations.length;
            for (const registration of registrations) {
                console.log("unregistering: %o", registration);
                registration.unregister()
                    .then(() => {
                        console.log("unregistered");
                        window.location.reload();
                    });

            }
            return count;
        });


    }

    async function browserAskPermission() {
        /* do we need still need to do it like this?  */
        return Notification.requestPermission()
            .then((result) => {
                if(result === 'granted') {
                    return result;
                }
                else {
                    console.error("We weren't granted permission: %o", result);
                    throw "We weren't granted permission.";
                }
            });
    }


    /* When we call this, the user is expecting it (they clicked a button) */
    async function askForPermission() {
        return browserAskPermission().then(() => {
            Success("Great, thanks.");
            registerServiceWorker();
        }).catch((err) => {
            Error("Your browser denied permission to send notifications.");
            console.error("askForPermission: %o", err);
        });
    }

    const data = {
        isSubscribed: isSubscribed,
        askForPermission: askForPermission,
        unsupported: unsupported,
        // recents: recents,
        // addRecent: addRecent,
        clearServiceWorker: clearServiceWorker
    };

    return (
        <ServiceWorkerContext.Provider value={data}>{children}</ServiceWorkerContext.Provider>
    );

});
